import { formatCents } from "./figures";
import type { KnowledgeGaps } from "./knowledgeGaps";
import { tallyOutcomeCents, type SalesBucket, type SalesOutcomeRow } from "./salesMeasure";
import { OUTCOMES, type Outcome } from "./types";

/**
 * The weekly nudge (Composer 4.5), verbatim: "threads that went quiet without
 * an outcome surface in the weekly digest, 'these 4 conversations need a tag',
 * never more than a few taps of debt."
 *
 * `needsOutcomeTag` already decides WHICH threads those are, and this module
 * does not repeat that decision. What it adds is the shape of the digest: an
 * order, a reading cap, and the two counts that keep the cap honest.
 *
 * The cap is the part worth explaining. "Never more than a few taps of debt"
 * is a claim about the system, not permission to truncate the list. A digest
 * that shows ten of thirty-seven and says ten is exactly section 0's silent
 * degradation, and the debt it hides is the debt that matters. So the list is
 * capped for reading and the total is never capped at all, and the difference
 * has its own field so no caller can render the short list without the number
 * being right there.
 *
 * AWAITING RATIFICATION (FINDINGS TA-93), like `THREAD_WINDOW_TURNS` before
 * it: the vault's illustration is four conversations and its bound is "a few".
 * Ten is a reading of "a few taps" that is generous rather than tight, chosen
 * because the failure of a cap set too low is a digest that is always in
 * overflow and therefore says nothing.
 */
export const DIGEST_LIST_CAP = 10;

export interface DigestThread {
  readonly threadId: string;
  readonly contactId: string;
  readonly displayName: string | null;
  /** The later of the two stamps: the moment the conversation went quiet. */
  readonly lastActivityAt: Date;
  /**
   * Who spoke last.
   *
   * Both kinds of thread are untagged and quiet, and they are not the same
   * debt. `shop` is an outcome nobody recorded. `customer` is a customer
   * nobody answered, and inviting the owner to tag that one `lost` files his
   * own dropped thread as the customer walking away.
   */
  readonly lastSpeaker: "customer" | "shop";
}

export interface OutcomeDigest {
  readonly periodStart: Date;
  readonly periodEnd: Date;
  /** Oldest silence first, capped at `DIGEST_LIST_CAP`. */
  readonly needsTag: readonly DigestThread[];
  /** Every thread that needs a tag. Never capped. */
  readonly needsTagTotal: number;
  /** What the cap hid. Zero on a digest inside the spec's promise. */
  readonly overflow: number;
  /** Of the total, how many are waiting on US rather than on a label. */
  readonly unanswered: number;
  /** Ghost labels the machine wrote this period, reported rather than hidden. */
  readonly autoGhosted: number;
  /**
   * The questions the shop could not answer (Agent Brains 13.4), ranked by
   * `rankKnowledgeGaps` and carried here as it came. It has its own cap and
   * its own overflow, so a short list in one section cannot hide a long one
   * in the other.
   */
  readonly knowledgeGaps: KnowledgeGaps;
  /**
   * How many owner notes the teach box holds open for this client, as a count
   * and nothing else (Training and Auto-Learn Master Spec B9: "ideas never
   * vanish into a void"). It is a snapshot taken when the digest is built, not
   * a figure about the closed week: a note has no period and no status past
   * open, so "open" is simply how many there are. No note text, name or id
   * reaches the digest, and nothing here reads a note (decision list item
   * 156, FINDINGS TA-514).
   */
  readonly ownerNotesOpen: number;
}

export interface OutcomeDigestInput {
  readonly threads: readonly DigestThread[];
  readonly autoGhosted: number;
  readonly since: Date;
  readonly now: Date;
  readonly cap?: number;
  /**
   * Already ranked. The gap section is not decided here: it is filed under
   * KB topics by the loader, which is the only code that reads a question, and
   * ranked by `rankKnowledgeGaps`. This function only keeps the digest whole,
   * and the field is required so no digest can be built that forgets it.
   */
  readonly knowledgeGaps: KnowledgeGaps;
  /**
   * Already counted, by the loader, as the number of open owner notes. Required
   * for the same reason `knowledgeGaps` is: so no digest can be built that
   * forgets the teach box. Only the number crosses: the digest is built field
   * by field, so nothing else a caller attaches can reach the owner.
   */
  readonly ownerNotesOpen: number;
}

export function buildOutcomeDigest(input: OutcomeDigestInput): OutcomeDigest {
  const cap = input.cap ?? DIGEST_LIST_CAP;

  const ordered = [...input.threads].sort((a, b) => {
    const byAge = a.lastActivityAt.getTime() - b.lastActivityAt.getTime();
    // A tie on the second must not swap two threads between two renders of
    // the same digest, so the id breaks it rather than the sort's stability,
    // which depends on the order the caller happened to read rows in.
    return byAge !== 0 ? byAge : a.threadId.localeCompare(b.threadId);
  });

  return {
    periodStart: input.since,
    periodEnd: input.now,
    needsTag: ordered.slice(0, cap),
    needsTagTotal: ordered.length,
    overflow: Math.max(0, ordered.length - cap),
    unanswered: ordered.filter((t) => t.lastSpeaker === "customer").length,
    autoGhosted: input.autoGhosted,
    knowledgeGaps: input.knowledgeGaps,
    ownerNotesOpen: input.ownerNotesOpen,
  };
}

/**
 * The week's recorded outcomes with their money (TA-508, SPEC_COVERAGE B1),
 * per label in integer cents. It is deliberately NOT a field of
 * `OutcomeDigest`: that shape is built field by field and pinned so nothing
 * rides in by accident, and the money has its own reader
 * (`collectDigestOutcomes`) and its own line. The rules are the sales
 * measure's, because the arithmetic is: never summed across labels (a deposit
 * and a later sold on one ring are both shown, TA-504), no total, no average,
 * no currency conversion, a row with no amount counted apart and never as
 * zero, a reversed row excluded.
 */
export type DigestOutcomes = Readonly<Record<Outcome, SalesBucket>>;

export function digestOutcomes(
  rows: readonly Pick<SalesOutcomeRow, "outcomeId" | "label" | "amountCents" | "reversedAt">[],
): DigestOutcomes {
  return tallyOutcomeCents(rows);
}

/**
 * One clause, `sold 2 ($2,000.50), deposit 1 ($300.00)`, every label with a
 * row in OUTCOMES order, each in its own cents through the one money module
 * (F-16), in the same shape `pnpm measure:sales` prints a bucket. Null when
 * nothing was recorded: a week with no outcome says nothing rather than a
 * zero.
 */
export function digestOutcomesLine(o: DigestOutcomes): string | null {
  const parts = OUTCOMES.filter((label) => o[label].count > 0).map((label) => {
    const b = o[label];
    return `${label} ${b.count} (${formatCents(b.amountCents)}${b.withoutAmount > 0 ? `, ${b.withoutAmount} without an amount` : ""})`;
  });
  return parts.length > 0 ? `outcomes recorded this week: ${parts.join(", ")}` : null;
}
